import { MutableRefObject, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Option, isNone, isSome, none } from 'fp-ts/lib/Option';

import { ErrorData } from 'domain/types';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutElementType, LayoutType } from 'domain/entity/layout';
import { ReadWidgetData } from 'use-case/widgets/data/read/readWidgetDataUseCase';
import { CriteriaCallback, useCase } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useBranding } from 'adapter/in/common/context/static/Branding';
import { useIntersect } from 'adapter/in/common/hooks/useIntersect';
import { WidgetCriteria, getWidgetBuilder } from 'adapter/in/common/widget/widgetBuilder';
import { getWidgetIssues, WidgetDataTypes } from 'domain/entity/widgetData';
import { WidgetIssue } from 'domain/entity/widgetIssue';
import { useWidgetDataCriteriaDependencies, useWidgetDataDependencies } from 'adapter/in/common/commonDependencies';
import Dashboard, { DashboardFrame } from 'domain/entity/dashboard';
import { getA4FooterText, getA4HeaderText } from 'adapter/in/reports/utils/A4Frame/A4Frame';
import { FrameWidgetData } from 'adapter/in/reports/components/CommonReportGrid/CommonReportGrid';
import { ReportInstance } from 'domain/entity/reportInstance';
import { useGeneratePDF } from 'adapter/in/reports/contexts/GeneratePDFContext';
import { WidgetType } from 'domain/entity/widget';
import RichTextData from 'domain/entity/widgets/RichTextData';
import { isLocalTextBlockId } from 'adapter/in/dashboards/components/Grid/localTextBlock';
import { isLocalBusinessInsightId } from 'adapter/in/dashboards/components/Grid/localBusinessInsight';

export const PAGES_PER_BATCH = 1;

export type WidgetDataError = Option<ErrorData>;
export type WidgetDataResult = Option<WidgetDataTypes>;

export const useWidgetData = (
    readWidgetData: ReadWidgetData
): {
    isWidgetDataLoading: boolean;
    widgetData: WidgetDataResult;
    widgetDataError: WidgetDataError;
    readWidgetData: CriteriaCallback;
    reset: () => void;
} => {
    const { isLoading, data, error, execute, reset } = useCase(readWidgetData);

    return {
        isWidgetDataLoading: isLoading,
        widgetData: data,
        widgetDataError: error,
        readWidgetData: execute,
        reset,
    };
};

export const useWidgetBuilder = (
    widgetCriteria: WidgetCriteria,
    dashboardId: string,
    type: string,
    updateFrameData?: Option<Dashboard>,
    widgetPage = 0,
    onReadWidgetDataDependencesChange?: (lastTimeUpdated: MutableRefObject<number>) => boolean,
    triggerRefreshTimestamp?: number,
    sizeIssue?: boolean,
    isEditEnabled?: boolean,
    frameData?: FrameWidgetData,
    reportTemplateData?: Dashboard,
    hideTableOverflowScrollbar = false
): {
    WidgetBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        isOnEdit?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement,
        widgetWidth?: number,
        widgetHeight?: number,
        isReportTemplate?: boolean
    ) => ReactElement;
    lastTimeUpdated: MutableRefObject<number>;
    handleReadWidgetData: () => void;
    branding: Branding;
    hasDataError: boolean;
    widgetIssues: WidgetIssue[];
    widgetTitle?: string;
} => {
    const [specificWidgetData, setSpecificWidgetData] = useState<WidgetDataResult>(none);
    const { readWidgetData: readWidgetDataUseCase } = useWidgetDataDependencies();
    const { buildWidgetDataCriteria, registerWidget, triggerWidgetLoads, reloadKey } = useWidgetDataCriteriaDependencies();
    const { profile } = useProfile();
    const { branding } = useBranding();
    const { currentPageBatch } = useGeneratePDF();
    const isLocalTextBlock = widgetCriteria.type === WidgetType.RICH_TEXT && isLocalTextBlockId(widgetCriteria.id);
    const isLocalBusinessInsight =
        widgetCriteria.type === WidgetType.BUSINESS_INSIGHT && isLocalBusinessInsightId(widgetCriteria.id);
    const isLocalRichTextWidget = isLocalTextBlock || isLocalBusinessInsight;

    const { referencer, entry } = useIntersect({
        root: null,
        threshold: 0.2,
        rootMargin: '0px',
    });
    const [markLoaded, setMarkLoaded] = useState<(() => void) | undefined>();
    const hasLoadedDueToPDF = useRef(false);
    const lastTimeUpdated = useRef(0);
    const lastReloadKey = useRef(reloadKey);
    const { isWidgetDataLoading, widgetData, widgetDataError: hasWidgetDataError, readWidgetData } = useWidgetData(readWidgetDataUseCase);

    useEffect(() => {
        if (!sizeIssue && !isLocalRichTextWidget) {
            const deregister = registerWidget?.();
            setMarkLoaded(() => deregister);
        }
    }, [sizeIssue, isLocalRichTextWidget]);

    const handleReadWidgetData = useCallback(
        () => widgetCriteria.id && !isLocalRichTextWidget && readWidgetData(buildWidgetDataCriteria(widgetCriteria.id, widgetCriteria.type)),
        [buildWidgetDataCriteria, isLocalRichTextWidget, readWidgetData, widgetCriteria.id, widgetCriteria.type]
    );

    const handleReadFrameWidgetData = useCallback(
        (frameText: string) =>
            widgetCriteria.id &&
            !isLocalRichTextWidget &&
            readWidgetData(buildWidgetDataCriteria(widgetCriteria.id, widgetCriteria.type, frameText)),
        [buildWidgetDataCriteria, isLocalRichTextWidget, readWidgetData, widgetCriteria.id, widgetCriteria.type]
    );

    const handleA4Frame = useCallback((frame: DashboardFrame | undefined, templateName?: string, reportInstance?: ReportInstance): boolean => {
        if (!frame) return false;

        if (type === LayoutElementType.A4_HEADER) {
            const text = getA4HeaderText(frame.header, templateName || '', profile, reportInstance);
            handleReadFrameWidgetData(text);
            return true;
        }

        if (type === LayoutElementType.A4_FOOTER) {
            const text = getA4FooterText(frame.footer, widgetCriteria.id, reportInstance);
            handleReadFrameWidgetData(text);
            return true;
        }

        return false;
    }, [handleReadFrameWidgetData, profile, type, widgetCriteria.id]);

    useEffect(() => {
        const hasId = !!widgetCriteria.id;
        const widgetInCurrentPage = widgetPage < (currentPageBatch + 1) * PAGES_PER_BATCH;
        const shouldForceByPDF =
            triggerWidgetLoads && !hasLoadedDueToPDF.current && (!reportTemplateData || widgetPage === undefined || widgetInCurrentPage);

        const shouldReloadForCriteria = reloadKey !== lastReloadKey.current;
        const shouldLoadNormally =
            entry.isIntersecting && (shouldReloadForCriteria || (onReadWidgetDataDependencesChange?.(lastTimeUpdated) ?? true));

        if (hasId && !isLocalRichTextWidget && (shouldForceByPDF || shouldLoadNormally)) {
            lastTimeUpdated.current = Date.now();
            lastReloadKey.current = reloadKey;

            const A4Frame = handleA4Frame(frameData?.frame, frameData?.templateName, frameData?.reportInstance);

            if (!A4Frame) {
                handleReadWidgetData();
            }

            if (shouldForceByPDF) {
                hasLoadedDueToPDF.current = true;
            }
        }
    }, [
        widgetCriteria.id,
        widgetCriteria.type,
        entry,
        triggerRefreshTimestamp,
        triggerWidgetLoads,
        onReadWidgetDataDependencesChange,
        reloadKey,
        frameData,
        type,
        isLocalRichTextWidget,
        handleA4Frame,
        handleReadWidgetData,
        widgetPage,
        currentPageBatch,
        reportTemplateData,
    ]);

    useEffect(() => {
        if (updateFrameData && isSome(updateFrameData)) {
            handleA4Frame(updateFrameData.value.frame, updateFrameData.value.name, frameData?.reportInstance);
        }
    }, [updateFrameData, handleA4Frame, frameData?.reportInstance]);

    useEffect(() => {
        if (isSome(widgetData)) {
            setSpecificWidgetData(widgetData);
            markLoaded?.();
        }
    }, [widgetData]);

    useEffect(() => {
        if (isSome(hasWidgetDataError)) {
            markLoaded?.();
        }
    }, [hasWidgetDataError]);

    const widgetDataForBuilder = () => {
        if (isLocalTextBlock) return new RichTextData('');
        if (isLocalBusinessInsight) return new RichTextData('', undefined, WidgetType.BUSINESS_INSIGHT);
        if (isSome(specificWidgetData)) return specificWidgetData.value;
        return {};
    };

    const widgetIssues = isSome(specificWidgetData) ? getWidgetIssues(specificWidgetData.value) : [];
    const widgetTitle = isSome(specificWidgetData)
        ? (specificWidgetData.value as { description?: { title?: string } }).description?.title
        : undefined;

    return {
        WidgetBuilder: getWidgetBuilder(
            { id: widgetCriteria.id, type: widgetCriteria.type, optionFields: widgetCriteria.optionFields },
            widgetDataForBuilder(),
            isLocalRichTextWidget ? false : isWidgetDataLoading || isNone(specificWidgetData),
            branding,
            profile.decimalSeparator,
            profile.thousandsSeparator,
            profile.dateFormat,
            profile.currencySymbolInLastPosition,
            referencer,
            isSome(hasWidgetDataError) ? hasWidgetDataError.value : undefined,
            dashboardId,
            isEditEnabled,
            hideTableOverflowScrollbar
        ),
        lastTimeUpdated,
        handleReadWidgetData,
        hasDataError: !!isSome(hasWidgetDataError),
        branding,
        widgetIssues,
        widgetTitle,
    };
};
